import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { formatDate } from "@/lib/formatDate";
import { parseCardMeta } from "@/lib/bankText";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import BankMatchSuggestionsCard from "@/components/BankMatchSuggestionsCard";
import { ReceiptThumbnail } from "@/components/ReceiptThumbnail";
import { BankDirectBookCard } from "@/components/BankDirectBookCard";
import { BankBookingRulesDialog } from "@/components/BankBookingRulesDialog";
import { MissingReceiptsDialog } from "@/components/MissingReceiptsDialog";
import { BankMatchDetailCard } from "@/components/BankMatchDetailCard";
import { BankPortalFetchHint } from "@/components/BankPortalFetchHint";
import { BankImportStatusCard } from "@/components/BankImportStatusCard";
import { useStickyState, useResetStickyState } from "@/hooks/useStickyState";
import { useCompany } from "@/context/CompanyContext";
import {
  Upload,
  ArrowDownCircle,
  ArrowUpCircle,
  Check,
  X,
  RefreshCw,
  Target,
  FileText,
  Receipt,
  FileInput,
  Loader2,
  AlertTriangle,
  AlertCircle,
  CreditCard,
  QrCode,
  Shuffle,
  History,
  ExternalLink,
  Search,
} from "lucide-react";
import { BankMatchActivityDialog } from "@/components/BankMatchActivityDialog";
import { ReceiptSourcesDialog } from "@/components/inbox/ReceiptSourcesDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const TYPE_LABEL = {
  receipt: { label: "Beleg", icon: Receipt, tone: "bg-amber-100 text-amber-800" },
  invoice: { label: "Kundenrechnung", icon: FileText, tone: "bg-sky-100 text-sky-800" },
  bill: { label: "Eingangsrechnung", icon: FileInput, tone: "bg-violet-100 text-violet-800" },
  direct: { label: "Direkt verbucht", icon: FileText, tone: "bg-emerald-100 text-emerald-800" },
  payout: { label: "Sammel-Auszahlung", icon: FileText, tone: "bg-emerald-100 text-emerald-800" },
};

function formatCHF(value) {
  const n = Number(value || 0);
  return `${n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ScoreBar({ score }) {
  const color =
    score >= 75 ? "bg-emerald-500" :
    score >= 50 ? "bg-amber-500" : "bg-slate-400";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, score)}%` }} />
      </div>
      <span className="text-[11px] font-medium text-slate-700 tabular-nums">{Math.round(score)}%</span>
    </div>
  );
}

export function BankReconciliation() {
  const navigate = useNavigate();
  // Iter118: reaktiv aus CompanyContext (vorher nur localStorage → kein Refresh bei Mandantenwechsel)
  const { company } = useCompany();
  const companyId = company?._id || localStorage.getItem("demoCompanyId");
  const [txs, setTxs] = useState([]);
  const [statusFilter, setStatusFilter] = useStickyState("bankrecon.statusFilter", "unmatched", companyId);
  const [directionFilter, setDirectionFilter] = useStickyState("bankrecon.directionFilter", "all", companyId);
  const resetBankReconView = useResetStickyState("bankrecon", companyId);
  const [selected, setSelected] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCand, setLoadingCand] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const [importResult, setImportResult] = useState(null); // {imported_count, auto_matched_count, ...}
  // Iter113: Re-Routing-Dialog State
  const [rerouteOpen, setRerouteOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [reroutePreview, setReroutePreview] = useState(null);
  const [rerouteLoading, setRerouteLoading] = useState(false);
  const [rerouteExecuting, setRerouteExecuting] = useState(false);

  // Iter161: Beleg-Foto-Zoom im Bank-Match
  const [zoomReceipt, setZoomReceipt] = useState(null);
  const [zoomBlobUrl, setZoomBlobUrl] = useState(null);
  const openReceiptZoom = async (candidate) => {
    // candidate hat id + label + type='receipt'
    const shim = {
      _id: candidate.id,
      mime_type: "image/jpeg",
      original_filename: candidate.label || "Beleg",
    };
    setZoomReceipt(shim);
    setZoomBlobUrl(null);
    try {
      const r = await apiClient.get(`/receipts/${candidate.id}/file`, {
        responseType: "blob",
      });
      setZoomBlobUrl(URL.createObjectURL(r.data));
    } catch {
      toast.error("Beleg-Foto konnte nicht geladen werden");
      setZoomReceipt(null);
    }
  };
  const [requestTx, setRequestTx] = useState(null);
  const [waContacts, setWaContacts] = useState([]);
  const [newWaName, setNewWaName] = useState("");
  const [newWaPhone, setNewWaPhone] = useState("");
  const [waSending, setWaSending] = useState(false);
  const [waError, setWaError] = useState(null);

  const openRequestDialog = async (tx) => {
    setRequestTx(tx);
    setWaError(null);
    try {
      const r = await apiClient.get(`/companies/${companyId}/receipt-request-contacts`);
      setWaContacts(r.data?.contacts || []);
    } catch { setWaContacts([]); }
  };

  const sendReceiptRequest = async (phone, name) => {
    if (!requestTx || !phone || waSending) return;
    setWaSending(true);
    setWaError(null);
    try {
      await apiClient.post(
        `/companies/${companyId}/bank-transactions/${requestTx.journal_entry_id}/request-receipt`,
        { phone, name: name || null }
      );
      toast.success(`Beleg-Anfrage per WhatsApp an ${name || phone} gesendet`);
      setRequestTx(null);
      setNewWaName("");
      setNewWaPhone("");
      loadTxs();
    } catch (err) {
      const detail = typeof err.response?.data?.detail === "string"
        ? err.response.data.detail
        : "WhatsApp-Anfrage fehlgeschlagen. Bitte Nummer prüfen und erneut versuchen.";
      setWaError(detail);
      toast.error(detail, { duration: 8000 });
    } finally {
      setWaSending(false);
    }
  };

  const deleteWaContact = async (phone) => {
    try {
      await apiClient.delete(
        `/companies/${companyId}/receipt-request-contacts?phone=${encodeURIComponent(phone)}`
      );
      setWaContacts((prev) => prev.filter((c) => c.phone !== phone));
    } catch {
      toast.error("Kontakt konnte nicht gelöscht werden");
    }
  };

  const closeReceiptZoom = () => {
    if (zoomBlobUrl) URL.revokeObjectURL(zoomBlobUrl);
    setZoomBlobUrl(null);
    setZoomReceipt(null);
  };

  const loadTxs = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      // Iter57: immer ALLE laden, client-side filtern → korrekte Counts in allen Tabs
      const res = await apiClient.get(
        `/companies/${companyId}/bank-transactions?status=all`
      );
      setTxs(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Fehler beim Laden der Banktransaktionen");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const refreshAll = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.post(`/companies/${companyId}/bank/rematch-invoices`);
      if (r.data?.matched) toast.success(`${r.data.matched} Rechnung(en) per Referenz mit Bank-Gutschriften abgeglichen`);
    } catch (e) {
      console.error(e);
    }
    loadTxs();
  }, [companyId, loadTxs]);

  useEffect(() => {
    loadTxs();
  }, [loadTxs]);

  const loadCandidates = async (tx) => {
    setSelected(tx);
    setCandidates([]);
    if (tx.matched) return;
    setLoadingCand(true);
    try {
      const res = await apiClient.get(`/bank-transactions/${tx.journal_entry_id}/match-candidates`);
      setCandidates(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error("Fehler beim Laden der Kandidaten");
    } finally {
      setLoadingCand(false);
    }
  };

  const applyMatch = async (cand) => {
    if (!selected) return;
    setApplyingId(cand.id);
    try {
      await apiClient.post(`/bank-transactions/${selected.journal_entry_id}/apply-match`, {
        candidate_type: cand.type,
        candidate_id: cand.id,
      });
      toast.success(`Zugeordnet: ${TYPE_LABEL[cand.type]?.label || cand.type} · ${cand.label}`);
      setCandidates([]);
      setSelected(null);
      await loadTxs();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Zuordnung fehlgeschlagen");
    } finally {
      setApplyingId(null);
    }
  };

  const unmatch = async (tx) => {
    try {
      await apiClient.delete(`/bank-transactions/${tx.journal_entry_id}/match`);
      toast.success("Zuordnung aufgehoben");
      await loadTxs();
      if (selected?.journal_entry_id === tx.journal_entry_id) {
        setSelected(null);
        setCandidates([]);
      }
    } catch (e) {
      console.error(e);
      toast.error("Aufheben fehlgeschlagen");
    }
  };

  // Iter113: Re-Routing
  const loadReroutePreview = async () => {
    if (!companyId) return;
    setRerouteLoading(true);
    try {
      const res = await apiClient.get(`/companies/${companyId}/bank-rerouting/preview`);
      setReroutePreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vorschau fehlgeschlagen");
    } finally {
      setRerouteLoading(false);
    }
  };

  const openRerouteDialog = async () => {
    setRerouteOpen(true);
    await loadReroutePreview();
  };

  const executeReroute = async (ibans) => {
    if (!companyId) return;
    setRerouteExecuting(true);
    try {
      const res = await apiClient.post(`/companies/${companyId}/bank-rerouting/execute`, {
        statement_ibans: ibans,
      });
      const n = res.data.moved_total || 0;
      if (n === 0) {
        toast.info("Keine Transaktionen wurden verschoben.");
      } else {
        toast.success(`${n} Bank-Transaktion(en) in die korrekte(n) Firma(en) verschoben.`);
      }
      await loadTxs();
      await loadReroutePreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verschieben fehlgeschlagen");
    } finally {
      setRerouteExecuting(false);
    }
  };

  // Iter239/255: camt einlesen + KI-Verbuchung als Hintergrund-Job mit Polling
  const [autobookJob, setAutobookJob] = useState(null);
  const [autobookWarnings, setAutobookWarnings] = useState([]);
  const autobooking = autobookJob?.status === "running";

  const pollAutobookStatus = useCallback(async () => {
    if (!companyId) return null;
    try {
      const res = await apiClient.get(`/companies/${companyId}/camt-autobook-status`);
      return res.data;
    } catch {
      return null;
    }
  }, [companyId]);

  useEffect(() => {
    let active = true;
    pollAutobookStatus().then((job) => {
      if (active && job?.status === "running") setAutobookJob(job);
    });
    return () => { active = false; };
  }, [pollAutobookStatus]);

  useEffect(() => {
    if (autobookJob?.status !== "running") return undefined;
    const iv = setInterval(async () => {
      const job = await pollAutobookStatus();
      if (!job) return;
      setAutobookJob(job);
      if (job.status === "done") {
        toast.success(job.result?.message || "KI-Verbuchung abgeschlossen", { duration: 10000 });
        loadTxs();
      } else if (job.status === "failed") {
        toast.error(job.error || "KI-Verbuchung fehlgeschlagen");
      }
    }, 4000);
    return () => clearInterval(iv);
  }, [autobookJob?.status, pollAutobookStatus, loadTxs]);

  const handleAutobookUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (!companyId) {
      toast.error("Bitte zuerst einen Mandanten auswählen.");
      e.target.value = "";
      return;
    }
    const formData = new FormData();
    Array.from(files).forEach((f) => formData.append("files", f));
    const names = Array.from(files).map((f) => f.name).join(", ");
    e.target.value = "";
    const delays = [5000, 15000, 30000];
    for (let attempt = 0; ; attempt++) {
      try {
        const res = await apiClient.post(
          `/companies/${companyId}/bank-import-camt-autobook`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setAutobookJob({ status: "running", phase: "parsen", ...res.data });
        setAutobookWarnings(res.data?.warnings || []);
        if ((res.data?.skipped_files || []).length > 0) {
          toast.warning(`${res.data.skipped_files.length} Datei(en) wegen IBAN-Zuordnung übersprungen — Details siehe Hinweis.`, { duration: 9000 });
        } else if ((res.data?.warnings || []).length > 0) {
          toast.warning(res.data.warnings[0], { duration: 9000 });
        }
        toast.info("Import gestartet — die KI verbucht im Hintergrund. Fortschritt siehe Banner.", { duration: 6000 });
        return;
      } catch (err) {
        const status = err.response?.status;
        const transient = !err.response || status >= 500 || status === 429;
        if (transient && attempt < delays.length) {
          const wait = delays[attempt];
          toast.warning(`Server nicht erreichbar – Import von ${names} wird in ${wait / 1000}s automatisch wiederholt (${attempt + 1}/${delays.length})`, { duration: wait, id: "camt-retry" });
          await new Promise((r) => setTimeout(r, wait));
          continue;
        }
        const msg = !err.response
          ? "Server nicht erreichbar – Verbindung unterbrochen. Bitte Datei in einer Minute erneut importieren (es wurde nichts importiert)."
          : status === 413 ? "Datei zu gross (max. 25 MB pro camt-Datei)"
          : err.response?.data?.detail || `Auto-Verbuchung fehlgeschlagen (${status})`;
        toast.dismiss("camt-retry");
        toast.error(msg, { duration: 15000 });
        return;
      }
    }
  };

  const unmatchedCount = txs.filter((t) => !t.matched).length;
  // Iter57: Probleme = Bank-Reject ODER IBAN-Mismatch
  const problemCount = txs.filter((t) => t.bank_rejected || t.iban_mismatch).length;
  const [txSearch, setTxSearch] = useState("");
  const txMatches = (t, q) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    const hay = [t.description, t.creditor_name, t.debtor_name, t.matched_label, t.qr_reference, t.scor_reference, t.context_hint, t.date,
      t.date ? new Date(t.date).toLocaleDateString("de-CH") : "", t.bank_classified_account]
      .filter(Boolean).join(" ").toLowerCase();
    if (hay.includes(s)) return true;
    const num = parseFloat(s.replace(/'/g, "").replace(",", "."));
    if (!Number.isNaN(num) && t.amount != null) {
      const a = Math.abs(Number(t.amount));
      return Math.abs(a - Math.abs(num)) < 0.005 || String(a.toFixed(2)).startsWith(s.replace(/'/g, "").replace(",", "."));
    }
    return false;
  };
  // Liste je nach Filter
  const visibleTxs = (
    statusFilter === "matched"
      ? txs.filter((t) => t.matched)
      : statusFilter === "unmatched"
      ? txs.filter((t) => !t.matched)
      : statusFilter === "problems"
      ? txs.filter((t) => t.bank_rejected || t.iban_mismatch)
      : txs
  )
    .filter((t) => directionFilter === "all" || (directionFilter === "in" ? t.direction === "in" : t.direction !== "in"))
    .filter((t) => txMatches(t, txSearch));

  return (
    <div className="space-y-6" data-testid="bank-reconciliation-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Bank-Abgleich</h1>
          <p className="mt-1 text-sm text-slate-500">
            Banktransaktionen mit Belegen, Kunden- und Eingangsrechnungen zuordnen — automatisch gescored.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label
            className={companyId ? "cursor-pointer" : "cursor-not-allowed opacity-50"}
            data-testid="bank-autobook-label"
            title={companyId ? "camt.053 einlesen, KI kontiert und verbucht direkt (Doubletten werden übersprungen)" : "Bitte zuerst einen Mandanten auswählen"}
          >
            <input
              type="file"
              accept=".xml,.zip,application/zip,application/x-zip-compressed"
              multiple
              onChange={handleAutobookUpload}
              disabled={!companyId || autobooking}
              className="hidden"
              data-testid="bank-autobook-input"
            />
            <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-600 text-white rounded-md px-3 py-1.5 hover:bg-emerald-700" title="Bankauszug (camt.053-Datei) hochladen — die KI verbucht alle Bewegungen automatisch auf die passenden Konten">
              {autobooking ? "KI verbucht…" : "⚡ Import + KI-Verbuchung"}
            </span>
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={openRerouteDialog}
            data-testid="bank-reroute-button"
            title="Prüft, ob Bankbewegungen versehentlich in der falschen Firma gelandet sind (andere IBAN), und verschiebt sie in die richtige"
          >
            <Shuffle className="h-3.5 w-3.5 mr-1.5" />
            Umbuchen
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setActivityOpen(true)}
            data-testid="bank-match-activity-button"
            title="Protokoll der letzten Zuordnungen – jede lässt sich rückgängig machen"
          >
            <History className="h-3.5 w-3.5 mr-1.5" />
            Letzte Zuordnungen
          </Button>
          <BankMatchActivityDialog companyId={companyId} open={activityOpen} onOpenChange={setActivityOpen} onChanged={loadTxs} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSourcesOpen(true)}
            data-testid="bank-sources-button"
            title="Beleg-Portale: Direktlinks zu Rechnungsseiten (Netflix, Digitec, Galaxus …), eigene Webseiten hinzufügen und Auto-Abruf einrichten"
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Beleg-Portale
          </Button>
          <ReceiptSourcesDialog companyId={companyId} open={sourcesOpen} onOpenChange={(o) => { setSourcesOpen(o); if (!o) loadTxs(); }} />
          <Button
            variant="outline"
            size="sm"
            onClick={refreshAll}
            disabled={loading}
            data-testid="bank-refresh-button"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      <BankImportStatusCard companyId={companyId} refreshKey={`${txs.length}-${autobookJob?.status || ""}`} />

      {/* Iter255: KI-Import Fortschritts-Banner (Hintergrund-Job) */}
      {autobookWarnings.length > 0 && (
        <Card className="p-3 border border-rose-300 bg-rose-50" data-testid="autobook-iban-warnings">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-rose-900 space-y-1 min-w-0">
              <div className="font-semibold">IBAN-Schutz: Bankdaten wurden nicht in eine fremde Firma geleitet</div>
              {autobookWarnings.map((w, i) => (
                <div key={i} data-testid={`autobook-iban-warning-${i}`}>{w}</div>
              ))}
            </div>
            <button type="button" className="ml-auto text-rose-700 hover:underline text-xs" onClick={() => setAutobookWarnings([])} data-testid="autobook-iban-warnings-dismiss">Schliessen</button>
          </div>
        </Card>
      )}
      {autobookJob && autobookJob.status === "running" && (
        <Card className="p-4 border border-emerald-300 bg-emerald-50" data-testid="autobook-progress-banner">
          <div className="flex items-center gap-3">
            <Loader2 className="h-4 w-4 animate-spin text-emerald-700" />
            <div className="text-sm text-emerald-900">
              <span className="font-medium">KI-Verbuchung läuft im Hintergrund…</span>{" "}
              {autobookJob.phase === "parsen" && "Dateien werden eingelesen."}
              {autobookJob.phase === "ki-kontierung" && (
                <>
                  {autobookJob.fresh || 0} neue Bewegungen ({autobookJob.duplicates || 0} Doubletten übersprungen) — KI kontiert Batch{" "}
                  {autobookJob.llm_batches_done || 0}/{autobookJob.llm_batches_total || "?"}.
                </>
              )}
              {autobookJob.phase === "buchen" && `${autobookJob.booked || 0} Buchungen werden erstellt…`}
              {autobookJob.phase === "rechnungs-abgleich" && "Zahlungen werden mit offenen Rechnungen abgeglichen…"}
              <span className="text-emerald-700"> Du kannst die Seite verlassen — der Import läuft weiter.</span>
            </div>
          </div>
        </Card>
      )}

      {/* Iter55: Import-Ergebnis-Banner mit Saldo/IBAN-Warnungen */}
      {importResult && (
        <Card
          className={`p-4 border ${
            (importResult.consistency_warnings?.length || 0) +
              (importResult.iban_mismatch_warnings?.length || 0) >
            0
              ? "border-amber-300 bg-amber-50"
              : "border-emerald-300 bg-emerald-50"
          }`}
          data-testid="bank-import-result-banner"
        >
          <div className="flex items-start gap-3">
            {(importResult.consistency_warnings?.length || 0) +
              (importResult.iban_mismatch_warnings?.length || 0) >
            0 ? (
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            ) : (
              <Check className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-semibold text-slate-900 text-sm">
                  Import abgeschlossen — {importResult.statements_processed || 0}{" "}
                  Statement(s) verarbeitet
                </div>
                <button
                  type="button"
                  onClick={() => setImportResult(null)}
                  className="text-slate-400 hover:text-slate-700 text-sm"
                  data-testid="bank-import-banner-close"
                  aria-label="Schliessen"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="rounded bg-white px-2.5 py-1.5 border border-slate-200">
                  <div className="text-slate-500">Importiert</div>
                  <div className="text-base font-semibold text-slate-900 tabular-nums">
                    {importResult.imported_count || 0}
                  </div>
                </div>
                <div className="rounded bg-white px-2.5 py-1.5 border border-slate-200">
                  <div className="text-slate-500">Auto-zugeordnet</div>
                  <div className="text-base font-semibold text-emerald-700 tabular-nums">
                    {importResult.auto_matched_count || 0}
                  </div>
                </div>
                <div className="rounded bg-white px-2.5 py-1.5 border border-slate-200">
                  <div className="text-slate-500">Doubletten geblockt</div>
                  <div className="text-base font-semibold text-slate-900 tabular-nums">
                    {importResult.skipped_duplicates || 0}
                  </div>
                </div>
                <div className="rounded bg-white px-2.5 py-1.5 border border-slate-200">
                  <div className="text-slate-500">Bank-Reject-Flags</div>
                  <div className="text-base font-semibold text-slate-900 tabular-nums">
                    {importResult.skipped_rejected || 0}
                  </div>
                </div>
              </div>
              {/* Iter118: Zusammenfassung pro Zielfirma (über der Detailliste) */}
              {importResult.auto_routed?.length > 0 && (() => {
                const grouped = {};
                for (const r of importResult.auto_routed) {
                  const key = r.routed_to_company_id || r.statement_iban;
                  if (!grouped[key]) grouped[key] = { name: r.routed_to_company_name, iban: r.statement_iban, count: 0 };
                  grouped[key].count += 1;
                }
                const groups = Object.values(grouped);
                return (
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="bank-import-routing-summary">
                    {groups.map((g, i) => (
                      <div key={i} className="text-xs bg-white border border-emerald-300 rounded px-3 py-2 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-emerald-900">{g.name}</div>
                          <div className="text-slate-500 truncate">IBAN {g.iban}</div>
                        </div>
                        <div className="text-lg font-bold text-emerald-700 flex-shrink-0">{g.count}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Detail-Liste — collapsible */}
              {importResult.auto_routed?.length > 0 && (
                <details className="mt-2" data-testid="bank-import-routing-details">
                  <summary className="text-xs text-emerald-800 cursor-pointer select-none hover:underline">
                    {importResult.auto_routed.length} Detail-Zeilen anzeigen ▾
                  </summary>
                  <div className="mt-1 space-y-1 max-h-64 overflow-y-auto">
                  {(importResult.auto_routed || []).map((r, i) => (
                <div
                  key={`route-${i}`}
                  className="mt-2 flex items-start gap-2 text-xs text-emerald-900 bg-white border border-emerald-200 rounded px-2.5 py-1.5"
                  data-testid="bank-import-auto-routed"
                >
                  <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Auto-Routing:</span> {r.file} → <strong>{r.routed_to_company_name}</strong> (IBAN {r.statement_iban})
                  </div>
                </div>
              ))}
                  </div>
                </details>
              )}
              {(importResult.routing_ambiguous || []).map((r, i) => (
                <div
                  key={`amb-${i}`}
                  className="mt-2 flex items-start gap-2 text-xs text-rose-900 bg-rose-50 border border-rose-300 rounded px-2.5 py-1.5"
                  data-testid="bank-import-warning-ambiguous-iban"
                >
                  <AlertCircle className="h-3.5 w-3.5 text-rose-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">IBAN mehrdeutig – kein Auto-Routing:</span> {r.file} · IBAN {r.statement_iban} ist bei{" "}
                    {r.companies?.length} Firmen hinterlegt ({(r.companies || []).map((c) => c.name).join(", ")}).{" "}
                    {r.imported_to_company_id
                      ? "Import erfolgte in die aktuell gewählte Firma."
                      : "Datei wurde übersprungen – bitte in der richtigen Firma hochladen oder die IBAN bei den anderen Firmen korrigieren."}
                  </div>
                </div>
              ))}
              {(importResult.iban_mismatch_warnings || []).map((w, i) => (
                <div
                  key={`iban-${i}`}
                  className="mt-2 flex items-start gap-2 text-xs text-amber-900 bg-white border border-amber-200 rounded px-2.5 py-1.5"
                  data-testid="bank-import-warning-iban"
                >
                  <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">IBAN-Mismatch:</span> {w}
                  </div>
                </div>
              ))}
              {(importResult.consistency_warnings || []).map((w, i) => (
                <div
                  key={`cons-${i}`}
                  className="mt-2 flex items-start gap-2 text-xs text-amber-900 bg-white border border-amber-200 rounded px-2.5 py-1.5"
                  data-testid="bank-import-warning-consistency"
                >
                  <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">Saldo-Inkonsistenz:</span> {w}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Iter90: Auto-Match Vorschläge mit FX-Toleranz */}
      <BankMatchSuggestionsCard companyId={companyId} onMatched={loadTxs} />

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 flex-wrap justify-between" data-testid="bank-filter-tabs">
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { id: "unmatched", label: `Offen (${unmatchedCount})` },
            { id: "matched", label: "Abgeglichen" },
            { id: "all", label: "Alle" },
            { id: "problems", label: `Probleme (${problemCount})`, problem: true },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setStatusFilter(t.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors flex items-center gap-1.5 ${
                statusFilter === t.id
                  ? t.problem
                    ? "bg-rose-600 text-white"
                    : "bg-slate-900 text-white"
                  : t.problem && problemCount > 0
                  ? "bg-rose-50 text-rose-800 border border-rose-200 hover:bg-rose-100"
                  : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`bank-filter-${t.id}`}
            >
              {t.problem && <AlertTriangle className="h-3 w-3" />}
              {t.label}
            </button>
          ))}
          <span className="mx-1 h-5 w-px bg-slate-200" aria-hidden="true" />
          {[
            { id: "all", label: "Ein- & Ausgänge" },
            { id: "in", label: "Nur Gutschriften", cls: "text-emerald-800 border-emerald-200 bg-emerald-50 hover:bg-emerald-100", active: "bg-emerald-600 text-white border-emerald-600" },
            { id: "out", label: "Nur Belastungen", cls: "text-rose-800 border-rose-200 bg-rose-50 hover:bg-rose-100", active: "bg-rose-600 text-white border-rose-600" },
          ].map((d) => (
            <button
              key={d.id}
              type="button"
              onClick={() => setDirectionFilter(d.id)}
              title={d.id === "in" ? "Belastungen ausblenden" : d.id === "out" ? "Gutschriften ausblenden" : "Alle Bewegungen"}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                directionFilter === d.id ? (d.active || "bg-slate-900 text-white border-slate-900") : (d.cls || "bg-white text-slate-700 border-slate-200 hover:bg-slate-50")
              }`}
              data-testid={`bank-direction-${d.id}`}
            >
              {d.label}
            </button>
          ))}
          {statusFilter === "unmatched" && unmatchedCount > 0 && (
            <span className="text-[11px] text-slate-500" data-testid="bank-clearing-hint">
              Offene Bewegungen stehen bis zur Zuordnung auf 1099 «Unklare Beträge» (Durchlaufkonto)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <MissingReceiptsDialog
            companyId={companyId}
            onSelectEntry={(jid) => {
              const tx = txs.find((t) => t.journal_entry_id === jid);
              if (tx) { setStatusFilter("all"); setDirectionFilter("all"); loadCandidates(tx); }
            }}
          />
          <BankBookingRulesDialog companyId={companyId} />
          <label
            className="cursor-pointer rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100 transition-colors"
            title="Payrexx-Auszahlungsbericht (PDF/CSV/XLSX) hochladen — wird automatisch der Bank-Gutschrift zugeordnet. Tipp: Die Payrexx-Mail an die Beleg-E-Mail weiterleiten geht auch automatisch."
            data-testid="payout-report-upload-button"
          >
            📄 Payrexx-Bericht
            <input
              type="file"
              multiple
              accept=".pdf,.csv,.xlsx"
              className="hidden"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                e.target.value = "";
                for (const f of files) {
                  const fd = new FormData();
                  fd.append("file", f);
                  try {
                    const r = await apiClient.post(`/companies/${companyId}/payrexx-payouts/upload`, fd);
                    toast.success(`${f.name} verarbeitet${r.data?.reference ? ` (Ref ${r.data.reference})` : ""}`);
                  } catch (err) {
                    toast.error(`${f.name}: ${err.response?.data?.detail || "Fehler"}`);
                  }
                }
                loadTxs();
              }}
            />
          </label>
        {statusFilter !== "unmatched" && (
          <button
            type="button"
            onClick={() => { setStatusFilter("unmatched"); resetBankReconView(); }}
            className="text-[11px] text-slate-500 hover:text-slate-700 underline"
            data-testid="bank-reset-view"
            title="Filter auf Standard zurücksetzen"
          >
            ↻ Standard
          </button>
        )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left: Bank transactions */}
        <Card className="p-4 lg:col-span-2" data-testid="bank-transactions-card">
          <div className="mb-3">
            <h2 className="text-sm font-semibold text-slate-800">Banktransaktionen</h2>
            <p className="text-[11px] text-slate-500">Klicken Sie auf eine Transaktion, um Match-Vorschläge zu sehen.</p>
            <div className="relative mt-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={txSearch}
                onChange={(e) => setTxSearch(e.target.value)}
                placeholder="Suchen: Name, Text, Betrag (z.B. 830.15), Datum, Referenz …"
                className="w-full rounded-md border border-slate-200 bg-white pl-8 pr-8 py-1.5 text-xs placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="bank-tx-search"
              />
              {txSearch && (
                <button type="button" onClick={() => setTxSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" title="Suche löschen" data-testid="bank-tx-search-clear">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {txSearch && <div className="mt-1 text-[11px] text-slate-500" data-testid="bank-tx-search-count">{visibleTxs.length} Treffer für «{txSearch}»</div>}
          </div>

          {loading ? (
            <div className="py-8 text-center text-xs text-slate-400">Lade …</div>
          ) : visibleTxs.length === 0 ? (
            <div className="py-10 text-center text-xs text-slate-400" data-testid="bank-transactions-empty">
              {statusFilter === "problems"
                ? "Keine Probleme — alle Buchungen sind sauber. ✓"
                : "Keine Banktransaktionen. Laden Sie einen camt.053-Bankauszug hoch."}
            </div>
          ) : (
            <ul className="space-y-2" data-testid="bank-transactions-list">
              {visibleTxs.map((tx) => {
                const isSelected = selected?.journal_entry_id === tx.journal_entry_id;
                const isIncoming = tx.direction === "in";
                return (
                  <li key={tx.journal_entry_id}>
                    <button
                      type="button"
                      onClick={() => loadCandidates(tx)}
                      className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
                        isSelected
                          ? "border-slate-900 bg-slate-50"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid={`bank-tx-${tx.journal_entry_id}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className={`mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isIncoming ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                        }`}>
                          {isIncoming ? <ArrowDownCircle className="h-4 w-4" /> : <ArrowUpCircle className="h-4 w-4" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-slate-500">
                              {formatDate(tx.date)}
                              {parseCardMeta(tx.description).purchaseDate && (
                                <span className="text-violet-700 font-medium" data-testid={`bank-tx-purchase-date-${tx.journal_entry_id}`}>
                                  {" "}· Kauf {formatDate(parseCardMeta(tx.description).purchaseDate)}
                                </span>
                              )}
                            </span>
                            <span className={`text-sm font-semibold tabular-nums ${
                              isIncoming ? "text-emerald-700" : "text-rose-700"
                            }`}>
                              {isIncoming ? "+" : "-"}{formatCHF(Math.abs(tx.amount))}
                            </span>
                          </div>
                          <div className="text-sm text-slate-800 truncate mt-0.5">
                            {tx.description || "—"}
                          </div>
                          {!tx.matched && tx.settled_hint && (
                            <div
                              className="text-[11px] text-emerald-700 mt-0.5 flex items-center gap-1"
                              data-testid={`bank-tx-settled-${tx.journal_entry_id}`}
                              title="Diese Gutschrift gehört zu einer Rechnung, deren Zahlung bereits von der Partner-App gemeldet wurde — nichts zu tun."
                            >
                              <span className="inline-flex items-center bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5 font-medium">
                                🟢 Bereits via App verbucht
                              </span>
                              <span className="truncate">{tx.settled_hint}</span>
                            </div>
                          )}
                          {!tx.matched && tx.portal_hint && (
                            <BankPortalFetchHint companyId={companyId} tx={tx} onChanged={loadTxs} />
                          )}
                          {!tx.matched && tx.inbox_credit_note && (
                            <div className="text-[11px] text-emerald-800 mt-0.5 flex items-center gap-2 flex-wrap rounded border border-emerald-200 bg-emerald-50 px-2 py-1" data-testid={`bank-tx-inbox-credit-${tx.journal_entry_id}`}>
                              <span>↩️ Rückgabe-Quittung liegt im Posteingang: «{tx.inbox_credit_note.file_name}» ({tx.inbox_credit_note.supplier}, CHF {Number(tx.inbox_credit_note.amount || 0).toFixed(2)}). Dort bestätigen → diese Gutschrift wird automatisch verknüpft und als Aufwand-Korrektur statt Ertrag gebucht.</span>
                              <button type="button" className="underline underline-offset-2 font-medium hover:text-emerald-950" onClick={(e) => { e.stopPropagation(); navigate(`/inbox?highlight=${tx.inbox_credit_note.id}`); }} data-testid={`bank-tx-inbox-credit-open-${tx.journal_entry_id}`}>Zum Posteingang</button>
                            </div>
                          )}
                          {!tx.matched && tx.context_hint && (
                            <div className="text-[11px] text-amber-700 mt-0.5 flex items-center gap-2 flex-wrap" data-testid={`bank-tx-hint-${tx.journal_entry_id}`}>
                              <span>💡 {tx.context_hint}</span>
                              {tx.receipt_no_receipt_at ? (
                                <span className="text-rose-700 font-medium" data-testid={`bank-tx-no-receipt-${tx.journal_entry_id}`}>
                                  ⚠️ Mitarbeiter hat keinen Beleg (Antwort vom {tx.receipt_no_receipt_at.slice(0, 10)})
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    className="ml-2 cursor-pointer text-slate-500 hover:text-slate-700 underline underline-offset-2 font-normal"
                                    onClick={(e) => { e.stopPropagation(); openRequestDialog(tx); }}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); openRequestDialog(tx); } }}
                                  >
                                    erneut anfragen
                                  </span>
                                </span>
                              ) : tx.receipt_requested_at ? (
                                <span className="text-emerald-700 font-medium" data-testid={`bank-tx-requested-${tx.journal_entry_id}`}>
                                  ✓ Beleg angefordert am {tx.receipt_requested_at.slice(0, 10)}
                                  {tx.receipt_requested_phone ? ` (${tx.receipt_requested_phone})` : ""}
                                  {tx.receipt_reminder_sent_at ? ` · Erinnerung am ${tx.receipt_reminder_sent_at.slice(0, 10)}` : ""}
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    className="ml-2 cursor-pointer text-slate-500 hover:text-slate-700 underline underline-offset-2 font-normal"
                                    onClick={(e) => { e.stopPropagation(); openRequestDialog(tx); }}
                                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); openRequestDialog(tx); } }}
                                  >
                                    erneut
                                  </span>
                                </span>
                              ) : (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  className="cursor-pointer text-emerald-700 hover:text-emerald-900 font-medium underline underline-offset-2"
                                  onClick={(e) => { e.stopPropagation(); openRequestDialog(tx); }}
                                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); openRequestDialog(tx); } }}
                                  data-testid={`bank-tx-request-receipt-${tx.journal_entry_id}`}
                                >
                                  📲 Beleg per WhatsApp anfordern
                                </span>
                              )}
                            </div>
                          )}
                          {/* Iter55: Counterparty + Reject-Badge + Karten-Badge + QR-Ref-Indikator */}
                          {(tx.creditor_name || tx.debtor_name) && (
                            <div className="text-[11px] text-slate-500 truncate mt-0.5">
                              {tx.direction === "in" ? "von " : "an "}
                              <span className="font-medium text-slate-700">
                                {tx.creditor_name || tx.debtor_name}
                              </span>
                            </div>
                          )}
                          <div className="mt-1 flex items-center gap-1 flex-wrap">
                            {tx.bank_rejected && (
                              <Badge
                                className="bg-rose-100 text-rose-800 text-[10px] h-4 px-1.5 border border-rose-300"
                                data-testid={`bank-tx-rejected-${tx.journal_entry_id}`}
                              >
                                <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                                Bank-Reject {tx.reject_code ? `(${tx.reject_code})` : ""}
                                {tx.error_code && tx.error_code !== "000"
                                  ? ` · Err ${tx.error_code}`
                                  : ""}
                              </Badge>
                            )}
                            {tx.iban_mismatch && (
                              <Badge
                                className="bg-amber-100 text-amber-900 text-[10px] h-4 px-1.5 border border-amber-300"
                                data-testid={`bank-tx-iban-mismatch-${tx.journal_entry_id}`}
                                title="Statement-IBAN weicht von der Firmen-IBAN ab"
                              >
                                <AlertCircle className="h-2.5 w-2.5 mr-0.5" />
                                IBAN-Mismatch
                              </Badge>
                            )}
                            {tx.bk_tx_family === "CCRD" && (
                              <Badge
                                className="bg-violet-100 text-violet-800 text-[10px] h-4 px-1.5"
                                data-testid={`bank-tx-card-${tx.journal_entry_id}`}
                              >
                                <CreditCard className="h-2.5 w-2.5 mr-0.5" />
                                Karte
                              </Badge>
                            )}
                            {(tx.qr_reference || tx.scor_reference) && (
                              <Badge
                                className="bg-sky-100 text-sky-800 text-[10px] h-4 px-1.5"
                                data-testid={`bank-tx-qr-${tx.journal_entry_id}`}
                                title={tx.qr_reference || tx.scor_reference}
                              >
                                <QrCode className="h-2.5 w-2.5 mr-0.5" />
                                {tx.qr_reference ? "QR-Ref" : "SCOR"}
                              </Badge>
                            )}
                          </div>
                          {tx.matched ? (
                            <div className="mt-1 flex items-center gap-1.5">
                              <Badge className="bg-emerald-100 text-emerald-800 text-[10px] h-4 px-1.5">
                                <Check className="h-2.5 w-2.5 mr-0.5" />
                                {TYPE_LABEL[tx.matched_type]?.label || tx.matched_type || "Abgeglichen"}
                              </Badge>
                              {tx.matched_via === "auto" && (
                                <Badge
                                  className="bg-sky-100 text-sky-800 text-[10px] h-4 px-1.5"
                                  title="Beim camt-Import automatisch per QR-Referenz zugeordnet"
                                  data-testid={`bank-tx-auto-matched-${tx.journal_entry_id}`}
                                >
                                  ⚡ Automatisch (QR)
                                </Badge>
                              )}
                              {tx.matched_via === "app" && (
                                <Badge
                                  className="bg-emerald-100 text-emerald-800 text-[10px] h-4 px-1.5"
                                  title="Zahlung wurde bereits von der Partner-App gemeldet — Bank-Gutschrift automatisch zugeordnet, keine Doppelbuchung"
                                  data-testid={`bank-tx-app-matched-${tx.journal_entry_id}`}
                                >
                                  🟢 Via App verbucht
                                </Badge>
                              )}
                              {tx.matched_via === "manuell" && (
                                <Badge
                                  className="bg-slate-100 text-slate-600 text-[10px] h-4 px-1.5"
                                  title="Von dir im Auto-Match bestätigt"
                                  data-testid={`bank-tx-manual-matched-${tx.journal_entry_id}`}
                                >
                                  Manuell bestätigt
                                </Badge>
                              )}
                              {tx.bank_classified && (
                                <Badge
                                  className="bg-violet-100 text-violet-800 text-[10px] h-4 px-1.5"
                                  title={`Automatisch verbucht (Quelle: ${tx.bank_classified_source || "KI"})`}
                                  data-testid={`bank-tx-ki-booked-${tx.journal_entry_id}`}
                                >
                                  🤖 KI-verbucht{tx.bank_classified_account ? ` · ${tx.bank_classified_account}` : ""}
                                  {tx.bank_classified_source ? ` (${tx.bank_classified_source})` : ""}
                                </Badge>
                              )}
                              <span className="text-[11px] text-slate-500 truncate">{tx.matched_label}</span>
                              {tx.payout_report && (
                                <span
                                  className="flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-800"
                                  title={`Payrexx-Auszahlungsbericht ${tx.payout_report.reference || ""}: Brutto CHF ${Number(tx.payout_report.gross || 0).toFixed(2)} − Gebühren CHF ${Number(tx.payout_report.fees || 0).toFixed(2)} = Netto CHF ${Number(tx.payout_report.net || 0).toFixed(2)}`}
                                  data-testid={`bank-tx-payout-report-${tx.journal_entry_id}`}
                                >
                                  📄 Beleg {tx.payout_report.reference || ""}
                                  {tx.payout_report.invoices?.length > 0 && ` · ${tx.payout_report.invoices.join(", ")}`}
                                  {tx.payout_report.pdf && (
                                    <button
                                      type="button"
                                      className="underline hover:text-emerald-950"
                                      title="Auszahlungsbericht-PDF herunterladen"
                                      data-testid={`payout-report-pdf-${tx.journal_entry_id}`}
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const res = await apiClient.get(`/companies/${companyId}/payrexx-payouts/${tx.payout_report.id}/file`, { responseType: "blob" });
                                          const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                                          const a = document.createElement("a");
                                          a.href = url; a.download = `Auszahlungsbericht_${tx.payout_report.reference || tx.date}.pdf`;
                                          document.body.appendChild(a); a.click(); document.body.removeChild(a);
                                          window.URL.revokeObjectURL(url);
                                        } catch { /* noop */ }
                                      }}
                                    >
                                      PDF
                                    </button>
                                  )}
                                </span>
                              )}
                              {tx.matched_type !== "payout" && (
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  unmatch(tx);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    unmatch(tx);
                                  }
                                }}
                                className="ml-auto text-[11px] text-rose-600 hover:underline cursor-pointer"
                                data-testid={`bank-unmatch-${tx.journal_entry_id}`}
                              >
                                Aufheben
                              </span>
                              )}
                            </div>
                          ) : (
                            <div className="mt-1">
                              <Badge className="bg-amber-100 text-amber-800 text-[10px] h-4 px-1.5">
                                Offen
                              </Badge>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* Right: Match candidates */}
        <Card className="p-4 lg:col-span-3" data-testid="bank-candidates-card">
          {!selected ? (
            <div className="py-16 text-center">
              <Target className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">
                Wählen Sie eine Banktransaktion links, um Vorschläge zu sehen.
              </p>
            </div>
          ) : selected.matched ? (
            <div className="py-6" data-testid="bank-candidates-already-matched">
              <div className="text-center mb-4">
                <Check className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
                <p className="text-sm text-slate-700">Diese Transaktion ist bereits abgeglichen.</p>
              </div>
              <BankMatchDetailCard companyId={companyId} entryId={selected.journal_entry_id} />
              <div className="text-center mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => unmatch(selected)}
                  data-testid="bank-selected-unmatch"
                >
                  <X className="h-3.5 w-3.5 mr-1.5" />
                  Zuordnung aufheben
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-4 pb-3 border-b border-slate-200">
                <div className="text-[11px] text-slate-500 uppercase tracking-wider">Ausgewählte Transaktion</div>
                <div className="flex items-center justify-between mt-1">
                  <div className="font-medium text-slate-900">{selected.description}</div>
                  <div className={`font-semibold tabular-nums ${
                    selected.direction === "in" ? "text-emerald-700" : "text-rose-700"
                  }`}>
                    {selected.direction === "in" ? "+" : "-"}{formatCHF(Math.abs(selected.amount))}
                  </div>
                </div>
                <div className="text-xs text-slate-500 mt-0.5" data-testid="bank-selected-dates">
                  {parseCardMeta(selected.description).purchaseDate ? (
                    <>Belastet: {formatDate(selected.date)} · <span className="text-violet-700 font-medium">Kauf: {formatDate(parseCardMeta(selected.description).purchaseDate)}</span></>
                  ) : (
                    <>Datum: {formatDate(selected.date)}</>
                  )}
                </div>
              </div>

              {loadingCand ? (
                <div className="py-8 text-center text-xs text-slate-400">Suche Kandidaten …</div>
              ) : candidates.length === 0 ? (
                <div className="py-6 text-center" data-testid="bank-candidates-empty">
                  <div className="text-sm text-slate-500">Keine passenden Vorschläge gefunden.</div>
                  <div className="text-xs text-slate-400 mt-1">
                    Beleg/Rechnung erstellen — oder unten direkt per KI verbuchen.
                  </div>
                </div>
              ) : (
                <ul className="space-y-2" data-testid="bank-candidates-list">
                  {candidates.map((c, idx) => {
                    const meta = TYPE_LABEL[c.type] || { label: c.type, icon: FileText, tone: "bg-slate-100 text-slate-800" };
                    const Icon = meta.icon;
                    return (
                      <li
                        key={`${c.type}-${c.id}`}
                        className="rounded-lg border border-slate-200 p-3 hover:bg-slate-50"
                        data-testid={`bank-candidate-${c.id}`}
                      >
                        <div className="flex items-start gap-3">
                          {c.type === "receipt" ? (
                            <ReceiptThumbnail
                              receipt={{
                                _id: c.id,
                                mime_type: "image/jpeg",
                                file_path: "unknown",
                              }}
                              onZoom={() => openReceiptZoom(c)}
                              size={56}
                              data-testid={`bank-candidate-thumb-${c.id}`}
                            />
                          ) : (
                            <div className="mt-0.5 h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <Icon className="h-4 w-4 text-slate-700" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5">
                                <Badge className={`${meta.tone} text-[10px] h-4 px-1.5`}>{meta.label}</Badge>
                                {idx === 0 && candidates.length > 1 && c.score >= 75 && (
                                  <Badge className="bg-slate-900 text-white text-[10px] h-4 px-1.5">Beste Übereinstimmung</Badge>
                                )}
                              </div>
                              <span className="text-sm font-semibold tabular-nums text-slate-900">
                                {formatCHF(c.amount)}
                              </span>
                            </div>
                            <div className="text-sm text-slate-800 mt-1 truncate">{c.label}</div>
                            {c.date && <div className="text-[11px] text-slate-500">Datum: {formatDate(c.date)}</div>}
                            <div className="mt-2">
                              <ScoreBar score={c.score} />
                            </div>
                            {c.reasons && c.reasons.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {c.reasons.map((r, i) => (
                                  <span
                                    key={i}
                                    className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded"
                                  >
                                    {r}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="mt-2 flex justify-end">
                              <Button
                                size="sm"
                                onClick={() => applyMatch(c)}
                                disabled={applyingId === c.id}
                                className="rounded-full bg-slate-900 text-slate-50 hover:bg-slate-800"
                                data-testid={`bank-candidate-apply-${c.id}`}
                              >
                                {applyingId === c.id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <>
                                    <Check className="h-3.5 w-3.5 mr-1" />
                                    Zuordnen
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {!selected.matched && (
                <BankDirectBookCard
                  companyId={companyId}
                  tx={selected}
                  onBooked={async () => {
                    setSelected(null);
                    await loadTxs();
                  }}
                />
              )}
            </>
          )}
        </Card>
      </div>

      {/* Iter113: Re-Routing Dialog */}
      <Dialog open={rerouteOpen} onOpenChange={setRerouteOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bank-reroute-dialog">
          <DialogHeader>
            <DialogTitle>Bank-Transaktionen umbuchen</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Findet Bank-Einträge in dieser Firma, deren Statement-IBAN nicht zur Firmen-IBAN passt,
            und verschiebt sie in die korrekte Firma (nur wenn du dort Schreibzugriff hast).
          </p>

          {rerouteLoading && <p className="text-sm text-slate-400 mt-3">Lade Vorschau…</p>}

          {reroutePreview && !rerouteLoading && (
            <div className="mt-4 space-y-4">
              <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2">
                <div><strong>Aktuelle Firma:</strong> {reroutePreview.current_company_name}</div>
                <div><strong>Hinterlegte IBAN:</strong> {reroutePreview.current_company_iban || "— (keine gesetzt)"}</div>
              </div>

              {reroutePreview.routable?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-emerald-800 mb-1">
                    Automatisch verschiebbar ({reroutePreview.routable.length})
                  </h3>
                  <ul className="space-y-2" data-testid="bank-reroute-routable-list">
                    {reroutePreview.routable.map((r) => (
                      <li key={r.statement_iban} className="border border-emerald-200 bg-emerald-50 rounded p-3 text-xs">
                        <div className="flex justify-between items-start gap-2">
                          <div>
                            <div><strong>Statement-IBAN:</strong> {r.statement_iban}</div>
                            <div><strong>Zielfirma:</strong> {r.target_company_name}</div>
                            <div><strong>Transaktionen:</strong> {r.transaction_count}</div>
                          </div>
                          <Button
                            size="sm"
                            disabled={rerouteExecuting}
                            onClick={() => executeReroute([r.statement_iban])}
                            data-testid={`bank-reroute-execute-${r.statement_iban}`}
                          >
                            Verschieben
                          </Button>
                        </div>
                        {r.sample_descriptions?.length > 0 && (
                          <div className="mt-1 text-[10px] text-slate-500 truncate">
                            Beispiele: {r.sample_descriptions.join(" · ").slice(0, 200)}…
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="mt-2 w-full"
                    disabled={rerouteExecuting}
                    onClick={() => executeReroute(null)}
                    data-testid="bank-reroute-execute-all"
                  >
                    {rerouteExecuting ? "Verschiebe…" : "Alle automatisch verschieben"}
                  </Button>
                </div>
              )}

              {reroutePreview.unroutable?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-amber-800 mb-1">
                    Nicht zuordenbar ({reroutePreview.unroutable.length})
                  </h3>
                  <p className="text-xs text-slate-500 mb-1">
                    Diese IBANs gehören zu keiner deiner Firmen. Hinterlege die IBAN in den
                    Firmen-Einstellungen — danach erneut prüfen.
                  </p>
                  <ul className="space-y-1" data-testid="bank-reroute-unroutable-list">
                    {reroutePreview.unroutable.map((r) => (
                      <li key={r.statement_iban} className="border border-amber-200 bg-amber-50 rounded p-2 text-xs">
                        <strong>{r.statement_iban}</strong> — {r.transaction_count} Transaktion(en)
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {reroutePreview.routable?.length === 0 && reroutePreview.unroutable?.length === 0 && (
                <p className="text-sm text-emerald-700 py-3 text-center">
                  ✓ Alle Bank-Einträge sind korrekt zugeordnet.
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter161: Beleg-Foto-Zoom im Bank-Match */}
      <Dialog open={!!zoomReceipt} onOpenChange={(open) => !open && closeReceiptZoom()}>
        <DialogContent className="max-w-3xl" data-testid="bank-receipt-zoom-dialog">
          <DialogHeader>
            <DialogTitle>
              Beleg-Vorschau
              {zoomReceipt?.original_filename && (
                <span className="ml-2 text-sm font-normal text-slate-500">
                  · {zoomReceipt.original_filename}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {zoomBlobUrl ? (
            <img
              src={zoomBlobUrl}
              alt="Beleg"
              className="w-full max-h-[70vh] object-contain rounded-md border border-slate-200"
              data-testid="bank-receipt-zoom-image"
            />
          ) : (
            <div className="h-48 flex items-center justify-center text-sm text-slate-500">
              Lade Beleg…
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter212: WhatsApp Beleg-Schnellanfrage mit Mitarbeiter-Nummern */}
      <Dialog open={!!requestTx} onOpenChange={(open) => !open && setRequestTx(null)}>
        <DialogContent className="max-w-md" data-testid="wa-request-dialog">
          <DialogHeader>
            <DialogTitle>Beleg per WhatsApp anfordern</DialogTitle>
          </DialogHeader>
          {requestTx && (
            <div className="space-y-3 w-full min-w-0 overflow-hidden">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 text-xs text-slate-700 overflow-hidden">
                <div className="font-medium truncate min-w-0">{requestTx.description || "Bankbewegung"}</div>
                <div className="mt-0.5 text-slate-500">
                  {formatDate(requestTx.date)} · CHF {formatCHF(Math.abs(requestTx.amount))}
                </div>
              </div>

              {waContacts.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-600 mb-1.5">Gespeicherte Nummern</p>
                  <ul className="space-y-1.5" data-testid="wa-contact-list">
                    {waContacts.map((c) => (
                      <li
                        key={c.phone}
                        className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5"
                        data-testid={`wa-contact-${c.phone}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800 truncate">{c.name || c.phone}</div>
                          {c.name && <div className="text-[11px] text-slate-500">{c.phone}</div>}
                        </div>
                        <Button
                          size="sm"
                          className="h-7 text-xs"
                          disabled={waSending}
                          onClick={() => sendReceiptRequest(c.phone, c.name)}
                          data-testid={`wa-contact-send-${c.phone}`}
                        >
                          {waSending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Senden"}
                        </Button>
                        <span
                          role="button"
                          tabIndex={0}
                          className="cursor-pointer text-slate-400 hover:text-rose-600"
                          title="Kontakt entfernen"
                          onClick={() => deleteWaContact(c.phone)}
                          onKeyDown={(e) => { if (e.key === "Enter") deleteWaContact(c.phone); }}
                          data-testid={`wa-contact-delete-${c.phone}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="pt-1 border-t border-slate-100">
                <p className="text-xs font-medium text-slate-600 mb-1.5">
                  {waContacts.length > 0 ? "Neue Nummer" : "WhatsApp-Nummer des Mitarbeiters"}
                </p>
                <div className="space-y-1.5">
                  <input
                    type="text"
                    value={newWaName}
                    onChange={(e) => setNewWaName(e.target.value)}
                    placeholder="Name (z.B. Max Muster)"
                    className="w-full h-8 rounded-md border border-slate-200 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                    data-testid="wa-new-name-input"
                  />
                  <div className="flex gap-1.5">
                    <input
                      type="tel"
                      value={newWaPhone}
                      onChange={(e) => setNewWaPhone(e.target.value)}
                      placeholder="+41 79 123 45 67"
                      className="flex-1 h-8 rounded-md border border-slate-200 px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                      data-testid="wa-new-phone-input"
                    />
                    <Button
                      size="sm"
                      className="h-8 text-xs"
                      disabled={waSending || !newWaPhone.trim()}
                      onClick={() => sendReceiptRequest(newWaPhone.trim(), newWaName.trim())}
                      data-testid="wa-new-send-btn"
                    >
                      {waSending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Senden"}
                    </Button>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Die Nummer wird für künftige Anfragen gespeichert.
                </p>
                {waError && (
                  <div
                    className="mt-2 rounded-md bg-rose-50 border border-rose-200 px-2.5 py-2 text-xs text-rose-800 break-words"
                    data-testid="wa-request-error"
                  >
                    {waError}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
